// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input, OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MatSelect} from '@angular/material/select';

export interface IColumnChooserElement {
  saveId?: string;  // passed in first element instead of in constructor.  this allows for saveId to change
  title: string;
  value: string;
  selected: boolean;
  hidden: boolean;
  apiName?: string;  // What the API calls this field
  txMap?: {[key: string | number]: string}; // translation map
  minWidth?: string;
}

@Component({
    selector: 'app-column-chooser',
    templateUrl: './column-chooser.component.html',
    styleUrls: ['./column-chooser.component.scss'],
    standalone: false
})
export class ColumnChooserComponent implements OnInit, OnChanges {
  @Input() saveId: string = null;
  @Input() elements: IColumnChooserElement[] = [];
  @Output() change: EventEmitter<string[]> = new EventEmitter<string[]>();
  @ViewChild(MatSelect) matSelect: MatSelect;

  columnMap: {[key: string]: IColumnChooserElement} = {};
  show = false;

  constructor(
      private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initColumns();
  }

  private initColumns() {
    if (this.elements && this.elements.length > 0 && this.elements[0].saveId != undefined) {
      this.saveId = this.elements[0].saveId;
    }
    this.setFromLocalStorage(this.loadFromLocalStorage());
    this.elements.forEach(el => this.columnMap[el.value] = el);
    this.change.emit(this.selectedValues());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.elements != undefined ) {
      this.elements = changes.elements.currentValue;
      this.initColumns();
    }
  }

  selectedValues(): string[] {
    return this.elements.filter(e => e.selected).map(e => e.value);
  }

  elementChanged(element: IColumnChooserElement): void {
    element.selected = !element.selected;
    this.change.emit(this.selectedValues());
    this.saveToLocalStorage();
  }

  showColumn(value: string): void {
    const el = this.columnMap[value];
    if (el) {
      const columnStatus = this.loadFromLocalStorage();
      let status;
      if (columnStatus === undefined) {
        status = true;
      } else if (columnStatus[value] === undefined) {
        status = true;
      } else {
        status = columnStatus[value];
      }
      el.hidden = false;
      el.selected = status;
      this.elements = Object.create(this.elements);
      this.changeDetectorRef.detectChanges();
      this.change.emit(this.selectedValues());
    }
  }

  hideColumn(value: string): void {
    const el: IColumnChooserElement = this.columnMap[value];
    if (el) {
      el.hidden = true;
      el.selected = false;
      this.elements = Object.create(this.elements);
      this.changeDetectorRef.detectChanges();
      this.change.emit(this.selectedValues());
    }
  }

  getLocalStorageId() {
    return `column-chooser-${this.saveId}`;
  }

  setFromLocalStorage(columnStatus) {
    if (!columnStatus) {
      return;
    }
    this.elements.forEach(el => {
      const status = columnStatus[el.value];
      if (status !== undefined) {
        el.selected = status;
      }
    });
  }

  loadFromLocalStorage(): { [key: string]: boolean } {
    const json = localStorage[this.getLocalStorageId()];
    if (json === undefined) {
      return;
    }
    return JSON.parse(json) as {[value: string]: boolean};
  }

  saveToLocalStorage(): void {
    const columnStatus = {};
    this.elements.forEach(el => {
      columnStatus[el.value] = el.selected;
    });
    localStorage[this.getLocalStorageId()] = JSON.stringify(columnStatus);
  }

}
